/**
 * 「系统参数运维」页（Sidebar「系统」分组，路由 ``/sys-config``）：后台配置项 CRUD
 * + 三个运维触发按钮（触发 daily / 批量刷新 running job / 最近 6h 地图生成）。
 *
 * 权限：``menu:sys-config``。菜单可见性由 Sidebar 同权限点决定，路由层
 * ``<RequirePermission>`` 兜底防 URL 直填；后端 ``/system-config/**``（含
 * ``trigger/*``）与 ``/map/recent/generate`` 挂同一个码硬门——页面与页内按钮
 * 同权限，避免「进得去页面但按钮 403」。
 *
 * ──────────────────────────────────────────────────────────────────────
 * 交互与布局约定（三条都是刻意为之，改动前请先读这段）
 * ──────────────────────────────────────────────────────────────────────
 *
 * 1. **按值的体量分形态，而不是按重要程度。** 单行值配置（两个运行包 URL /
 *    中台包模板 / daily base-version / Case-Flow 查询并发上限）走 ``ConfigRow``
 *    行式列表；值域封闭的两项——布尔开关（预测数据开放）与枚举下拉（创建任务
 *    默认预测数据版本）——走 ``ConfigSwitchRow`` / ``ConfigSelectRow``（控件即编辑态，
 *    不套「编辑」）；四个 JSON 配置（bazel 预设 / module_registry / 评测器白名单 /
 *    参数型评估器聚合声明）走全宽面板。早期实现把 7 项塞进同一个
 *    ``repeat(auto-fill, minmax(360px,1fr))`` 网格里，单行卡 ~120px、
 *    JSON 卡 500px+，同一行高度差四倍，网格被撑得七零八落。
 *
 * 2. **JSON 配置一律双视图。** 面板右上角 ``Segmented`` 切「表单 / JSON」，
 *    两个视图共用同一份 draft 字符串、双向同步，切换不丢改动：
 *    表单视图用 ``KvTableEditor`` 按行编辑（评测器白名单 30+ 条，靠它自带的搜索
 *    才找得到要改的那条）；JSON 视图用 ``JsonEditor``（行号 + 高亮 + 实时校验），
 *    校验不过时保存按钮直接 disable——不再像原来那样等点了保存才弹一句 message。
 *
 * 3. **左侧锚点导航常驻。** 每个配置项一条，条目前的状态点直接表达该项当前来源
 *    （实心 = Redis 覆盖，空心 = 代码默认）；页头概览徽标汇总「N 项 Redis 覆盖 /
 *    M 项代码默认」。点条目平滑滚到对应区块，滚动时反查高亮当前区块。
 *    各卡的 source 通过 ``shared.tsx`` 里的 ``SysConfigBus`` 上报，页头「全部刷新」
 *    也走同一条总线广播。
 *
 * 说明文案的摆放同样有讲究：一句话的功能描述**常显**为副标题 / 行内说明，
 * 只有结构说明与跨字段约束这类长文才收进 info 气泡。原实现把所有描述都埋进
 * Popover，等于默认谁都看不见。
 *
 * 文件拆分：``validators.ts``（与后端同口径的校验纯函数）/ ``shared.tsx``（面板
 * 外壳 + 跨卡总线 + 拉取 hook）/ ``ConfigCards.tsx`` / ``OpsCards.tsx`` /
 * ``index.less``（原来是 ``<style>{SC_CSS}</style>`` 内联注入，已搬成正常样式文件）。
 */
import {
  useCallback, useEffect, useMemo, useRef, useState, type RefObject,
} from 'react';
import { Button, Tooltip } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';

import {
  AlgorithmPackageUrlTemplateVo, CaseflowQueryConcurrencyVo, DailyBaseVersionVo,
  GenerateScenePackageUrlVo,
  PredictionJobDefaultVersionVo, PredictionOpenEnabledVo,
  getAlgorithmPackageUrlTemplate, getCaseflowQueryConcurrency, getDailyBaseVersion,
getGeneratePerceptionScenePackageUrl, getGenerateScenePackageUrl,
  getGenerateScenePreviewPackageUrl, getPerceptionSceneTreeRootId,
  getPredictionJobDefaultVersion, getPredictionOpenEnabled,
  resetAlgorithmPackageUrlTemplate, resetCaseflowQueryConcurrency, resetDailyBaseVersion,
  resetGeneratePerceptionScenePackageUrl, resetGenerateScenePackageUrl,
  resetGenerateScenePreviewPackageUrl, resetPerceptionSceneTreeRootId,
  resetPredictionJobDefaultVersion, resetPredictionOpenEnabled,
  updateAlgorithmPackageUrlTemplate, updateCaseflowQueryConcurrency, updateDailyBaseVersion,
  updateGeneratePerceptionScenePackageUrl, updateGenerateScenePackageUrl,
  updateGenerateScenePreviewPackageUrl, updatePerceptionSceneTreeRootId,
  updatePredictionJobDefaultVersion, updatePredictionOpenEnabled,
} from '@apis/simulation';

import {
  BazelPresetsCard, ConfigRow, ConfigSelectRow, ConfigSwitchRow, EvaluatorMetricAggCard,
  EvaluatorWhitelistCard, ModuleRegistryCard, OutputBucketRoutingCard,
} from './ConfigCards';
import {
  BulkRefreshRunningJobsCard, DailyJobTriggerCard, MapGenerateRecentCard, PartitionCleanupCard,
} from './OpsCards';
import { SysConfigBusProvider, useSysConfigBus } from './shared';
import {
  validateCaseflowQueryConcurrency, validateDailyBaseVersion, validatePackageUrl,
  validatePackageUrlTemplate,
  validatePredictionJobDefaultVersion, validatePredictionOpenEnabled,
} from './validators';
import JobCustomParametersCard from './JobCustomParametersCard';
import NdpModelSlotsCard from './NdpModelSlotsCard';
import ModelCategoriesCard from './ModelCategoriesCard';
import ModelUploadNamesCard from './ModelUploadNamesCard';
import './index.less';

/* ------------------------------------------------------------------ *
 * 锚点导航数据
 * ------------------------------------------------------------------ */

interface NavItem {
  /** 目标 DOM 元素 id；同时是配置项在 bus 里上报 source 的 key */
  id: string;
  label: string;
  /** 运维项没有「来源」概念，状态点固定用 amber 实心 */
  ops?: boolean;
}

interface NavGroup {
  /** 左侧导航的分组名，同时**就是**正文里那条分区标题——两处渲染同一个字符串 */
  title: string;
  /** 正文分区标题的锚点 id（`.sc-sec` 上） */
  secId: string;
  items: NavItem[];
}

/**
 * 导航与正文分区的**唯一**数据源。
 *
 * 以前 `NAV_GROUPS` 持有 10 个叶子锚点，而正文里的四条 `.sc-sec` 标题
 * （'运行包与版本' / 'Bazel 编译' / '评测器' / '运维操作'）是**另外**四个
 * 手写字符串。改一处漏一处时，导航分组名与正文标题就对不上了，而这两处
 * 恰恰是同一个东西——用户是靠"导航里写着 Bazel 编译"去正文里找"Bazel 编译"的。
 * 现在正文的分区标题由 `NAV_GROUPS[].title` 渲染，改名只改这一处。
 */
const NAV_GROUPS: NavGroup[] = [
  {
    title: '运行包与版本',
    secId: 'sec-packages',
    items: [
      { id: 'cfg-generate-scene', label: '场景生成运行包' },
      { id: 'cfg-perception-generate-scene', label: '感知场景生成运行包' },
      { id: 'cfg-perception-scene-tree', label: '感知场景子树根节点' },
      { id: 'cfg-scene-preview', label: '场景预览运行包' },
      { id: 'cfg-algorithm-template', label: '数据中台包模板' },
      { id: 'cfg-daily-base-version', label: 'daily base-version' },
    ],
  },
  {
    title: 'Bazel 编译',
    secId: 'sec-bazel',
    items: [
      { id: 'sec-bazel-presets', label: 'bazel 编译预设' },
      { id: 'sec-module-registry', label: 'module_registry' },
    ],
  },
  {
    title: '评测器',
    secId: 'sec-evaluator',
    items: [
      { id: 'sec-evaluator-whitelist', label: '评测器白名单' },
      { id: 'sec-evaluator-metric-agg', label: '评估器参数聚合声明' },
    ],
  },
  {
    title: '预测数据',
    secId: 'sec-prediction',
    items: [
      { id: 'cfg-prediction-open-enabled', label: '预测数据开放开关' },
      { id: 'cfg-prediction-job-default-version', label: '创建任务默认预测数据版本' },
    ],
  },
  {
    title: '任务参数',
    secId: 'sec-job-parameters',
    items: [{ id: 'sec-job-custom-parameters', label: '任务自定义参数' }],
  },
  {
    title: '产物桶',
    secId: 'sec-output-bucket',
    items: [
      { id: 'sec-output-bucket-routing', label: '产物桶分片路由表' },
    ],
  },
  {
    title: 'Case-Flow 查询',
    secId: 'sec-caseflow',
    items: [{ id: 'cfg-caseflow-query-concurrency', label: '查询并发上限' }],
  },
  {
    title: '模型同步',
    secId: 'sec-model-sync',
    items: [{ id: 'sec-ndp-model-slots', label: '中台同步忽略目录' }, { id: 'sec-model-categories', label: '模型分类' }, { id: 'sec-model-upload-names', label: '允许上传的模型类别' }],
  },
  {
    title: '运维操作',
    secId: 'sec-ops',
    items: [
      { id: 'ops-daily-job', label: '触发 daily 仿真', ops: true },
      { id: 'ops-bulk-refresh', label: '批量刷新 Job 状态', ops: true },
      { id: 'ops-map-generate', label: '触发地图生成', ops: true },
    ],
  },
];

const ALL_ANCHOR_IDS = NAV_GROUPS.flatMap((g) => g.items.map((i) => i.id));

/** 所有「配置项」（非运维）的锚点 id，用来统计概览徽标 */
const CONFIG_IDS = NAV_GROUPS.flatMap((g) => g.items.filter((i) => !i.ops).map((i) => i.id));

/** 配置项总数（徽标的分母）。徽标只给分子的话，用户分不清是 5/7 还是 7/7 */
const CONFIG_TOTAL = CONFIG_IDS.length;

/* ------------------------------------------------------------------ *
 * 布局几何：页头高度 / 导航可用高度的单一来源
 * ------------------------------------------------------------------ */

/**
 * CSS 自定义属性名。这几个名字必须与 `index.less` 里的声明逐字一致——
 * 那边的文件头约定 A 说得很清楚：sticky 几何只有一个来源。
 */
const VAR_TOPBAR_H = '--sc-topbar-h';
const VAR_NAV_MAX_H = '--sc-nav-max-h';
/** 锚点落点相对页头再留的呼吸量，与 less 里的 `--sc-anchor-gap` 是同一个数 */
const ANCHOR_GAP = 12;

/**
 * 找到本页**真正的**滚动容器。
 *
 * Sidebar 布局里内容区是 `.sidebar-content-inner`（`flex:1; overflow:auto`），
 * 视口本身不滚。所以：
 * - `max-height: calc(100vh - 96px)` 这种按视口算的尺寸是错的（外面还有
 *   Sidebar 头部，导航会比可视区高出一截，底部条目被裁掉还没有滚动条）；
 * - 判断「是否滚到底」也得问这个容器，不是 `window.scrollY`。
 *
 * 逐层往上找第一个 `overflow-y: auto|scroll` 的祖先，而不是把 `.sidebar-content-inner`
 * 这个类名硬编码进来——这页以后被塞进别的壳里也不会静默失效；一个都没找到
 * 就退回 `document.scrollingElement`（页面自身滚动的场景）。
 */
function findScrollParent(el: HTMLElement | null): HTMLElement | null {
  let node: HTMLElement | null = el?.parentElement ?? null;
  while (node) {
    const { overflowY } = window.getComputedStyle(node);
    if (overflowY === 'auto' || overflowY === 'scroll') return node;
    node = node.parentElement;
  }
  return (document.scrollingElement as HTMLElement | null) ?? null;
}

/**
 * 实测页头高度与滚动容器高度，写回 `.sc-root` 上的 CSS 变量。
 *
 * 返回 `anchorOffset`（页头高 + 呼吸量）给高亮判定线用——导航的 `top`、
 * 所有 `scroll-margin-top`、JS 的判定线于是全部出自同一个实测值。
 * 以前这四个数字是四个互不相干的字面量（48 / 64 / 76 / 120），锚点落在
 * 页头下 ~26px 而判定线在 120px，点导航跳过去高亮的却还是上一节。
 */
function useStickyGeometry(
  rootRef: RefObject<HTMLDivElement | null>,
  topbarRef: RefObject<HTMLDivElement | null>,
): { anchorOffset: number; scrollParent: HTMLElement | null } {
  const [anchorOffset, setAnchorOffset] = useState(62); // 50 + 12，与 less 兜底值一致
  const [scrollParent, setScrollParent] = useState<HTMLElement | null>(null);

  useEffect(() => {
    const root = rootRef.current;
    const topbar = topbarRef.current;
    if (!root || !topbar) return undefined;

    const parent = findScrollParent(root);
    setScrollParent(parent);

    const sync = () => {
      const h = Math.round(topbar.getBoundingClientRect().height);
      if (h > 0) {
        root.style.setProperty(VAR_TOPBAR_H, `${h}px`);
        setAnchorOffset(h + ANCHOR_GAP);
      }
      // 导航最多占滚动容器可视高度减去页头与上下留白，按**真实容器**算而不是 100vh
      const avail = parent ? parent.clientHeight : window.innerHeight;
      const navMax = Math.max(160, avail - (h > 0 ? h : 50) - ANCHOR_GAP - 24);
      root.style.setProperty(VAR_NAV_MAX_H, `${navMax}px`);
    };

    sync();

    // 页头会因为徽标文案变长（「3 项未保存」出现 / 消失）而换行变高，
    // 滚动容器会因为侧边栏折叠而变宽变高——两个都得盯着，不能只在 mount 量一次
    const ro = new ResizeObserver(sync);
    ro.observe(topbar);
    if (parent) ro.observe(parent);
    window.addEventListener('resize', sync);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', sync);
    };
  }, [rootRef, topbarRef]);

  return { anchorOffset, scrollParent };
}

/* ------------------------------------------------------------------ *
 * 当前区块高亮
 * ------------------------------------------------------------------ */

/**
 * 跟踪当前滚到哪个区块。
 *
 * 这里刻意**不用 IntersectionObserver**：本页真正的滚动容器是 Sidebar 布局里的
 * ``.sidebar-content-inner``（``overflow:auto``）而不是 viewport，IO 得先拿到那个
 * 容器当 root，拿不到就整个失效、且失效时静默无报错。直接量各锚点相对视口的
 * ``getBoundingClientRect().top``、取"最后一个越过判定线的"更简单也更稳，
 * 换布局也不会悄悄坏掉。rAF 节流保证滚动不掉帧。
 *
 * 两处必须显式处理，否则高亮会在两个位置**永远**错：
 *
 * 1. **判定线来自实测**（`line` 参数），和锚点的 `scroll-margin-top` 同源。
 * 2. **滚到底强制高亮最后一项**。最后一块（运维卡三列）后面只有
 *    `padding-bottom: 40px`，1366×768 上滚到底它的 top 还有 ~420px，
 *    永远越不过判定线——「触发地图生成」这一条点得进去却从不高亮。
 *    容器触底时直接认最后一个锚点，这也符合用户的直觉（看到底了就是最后一节）。
 *
 * `deps` 里带上 `revision`：7 张卡是异步加载的，数据到达后整页高度剧变，
 * 不重算的话高亮还停在初始那一帧的结论上。
 */
function useActiveAnchor(
  ids: string[],
  line: number,
  scrollParent: HTMLElement | null,
  revision: number,
): string {
  const [active, setActive] = useState(ids[0] ?? '');
  const tickingRef = useRef(false);

  useEffect(() => {
    const compute = () => {
      tickingRef.current = false;

      // 触底：最后一块再也滚不上来了，直接认它
      const sc = scrollParent;
      if (sc && sc.scrollHeight - sc.scrollTop - sc.clientHeight <= 2) {
        const last = ids[ids.length - 1];
        if (last) {
          setActive(last);
          return;
        }
      }

      let current = ids[0] ?? '';
      for (const id of ids) {
        const el = document.getElementById(id);
        if (!el) continue;
        if (el.getBoundingClientRect().top <= line) current = id;
      }
      setActive(current);
    };

    const onScroll = () => {
      if (tickingRef.current) return;
      tickingRef.current = true;
      requestAnimationFrame(compute);
    };

    // capture 阶段监听 document，滚动容器是谁都能收到（scroll 事件不冒泡）
    document.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    compute();
    return () => {
      document.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [ids, line, scrollParent, revision]);

  return active;
}

/**
 * 内容高度变化时给出一个递增的 revision。
 *
 * 七张卡是异步拉的：首帧全是 loading 占位（每张 ~60px），数据到齐后 JSON 卡
 * 撑到 400px+，整页高度能翻三倍。只在 mount 算一次高亮，结论对应的是那个
 * 早已不存在的布局。ResizeObserver 盯住内容容器，高度一变就让上面的 hook 重算。
 */
function useContentRevision(ref: RefObject<HTMLElement | null>): number {
  const [revision, setRevision] = useState(0);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const ro = new ResizeObserver(() => setRevision((r) => r + 1));
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);

  return revision;
}

/* ------------------------------------------------------------------ *
 * 页头 / 导航
 * ------------------------------------------------------------------ */

function TopBar({ innerRef }: { innerRef: RefObject<HTMLDivElement | null> }) {
  const { refreshAll, sources, failedIds, dirtyIds } = useSysConfigBus();
  const [spinning, setSpinning] = useState(false);

  const { redisCount, defaultCount, failedCount, dirtyCount } = useMemo(() => {
    let redis = 0;
    let def = 0;
    let failed = 0;
    for (const id of CONFIG_IDS) {
      if (id in failedIds) failed += 1;
      const s = sources[id];
      if (s === 'redis' || s === 'db') redis += 1;
      else if (s === 'code_default' || s === 'snapshot') def += 1;
    }
    // 脏计数把运维项也算进来无意义（它们没有 draft），只数配置项
    const dirty = CONFIG_IDS.filter((id) => id in dirtyIds).length;
    return { redisCount: redis, defaultCount: def, failedCount: failed, dirtyCount: dirty };
  }, [sources, failedIds, dirtyIds]);

  // 各卡自己带 loading 态，这里只做一个短暂的按钮反馈，不去汇总七张卡的进度
  const handleRefreshAll = () => {
    refreshAll();
    setSpinning(true);
    window.setTimeout(() => setSpinning(false), 600);
  };

  return (
    <div className="sc-topbar" ref={innerRef}>
      <h2 className="sc-topbar-title">系统参数运维</h2>
      <div className="sc-topbar-spacer" />
      {dirtyCount > 0 && (
        <span className="sc-unsaved" title="有配置项改动尚未保存">
          <i className="sc-dot" />
          {dirtyCount} 项未保存
        </span>
      )}
      <span className="sc-overview">
        {/* 带上分母：只给「2 项 Redis 覆盖 / 3 项代码默认」时，用户没法判断
            剩下那 2 项是不存在、还是没拉到——两者的处置完全不同 */}
        <span className="sc-pill redis">
          <i className="sc-dot" />
          {redisCount} / {CONFIG_TOTAL} 项已配置
        </span>
        <span className="sc-sep">/</span>
        <span className="sc-pill def">{defaultCount} 项默认配置</span>
        {failedCount > 0 && (
          <>
            <span className="sc-sep">/</span>
            <span className="sc-pill bad" title="这些配置项这次没拉到，点「全部刷新」重试">
              {failedCount} 项未加载
            </span>
          </>
        )}
      </span>
      <Tooltip title="重新拉取本页全部配置项">
        <Button size="small" icon={<ReloadOutlined spin={spinning} />} onClick={handleRefreshAll}>
          全部刷新
        </Button>
      </Tooltip>
    </div>
  );
}

function AnchorNav({ active }: { active: string }) {
  const { sources, failedIds, dirtyIds } = useSysConfigBus();

  const jump = useCallback((id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  return (
    <nav className="sc-nav" aria-label="页内导航">
      {NAV_GROUPS.map((group) => (
        <div className="sc-nav-group" key={group.title}>
          <div className="sc-nav-group-title">{group.title}</div>
          {group.items.map((item) => {
            // 状态点：运维项固定 amber；配置项按上报的 source 实心 / 空心，未加载则中性灰；
            // 拉取失败单列一档——它跟「代码默认」不是一回事，不能都画成空心
            const dotClass = item.ops
              ? 'ops'
              : (item.id in failedIds ? 'failed' : (sources[item.id] ?? ''));
            const dirty = item.id in dirtyIds;
            return (
              <button
                type="button"
                key={item.id}
                className={`sc-nav-item${item.ops ? ' ops' : ''}${active === item.id ? ' active' : ''}`}
                onClick={() => jump(item.id)}
                title={dirty ? `${item.label}（有未保存改动）` : item.label}
              >
                <i className={`sc-nav-dot ${dotClass}`} />
                <span className="sc-nav-label">{item.label}</span>
                {/* 脏标记单独一个点：sc-nav-dot 编码的是来源，不能兼职 */}
                {dirty && <i className="sc-nav-dirty" />}
              </button>
            );
          })}
        </div>
      ))}
    </nav>
  );
}

/**
 * 分区标题。标题文案与锚点 id 都取自 `NAV_GROUPS`，和左侧导航同源。
 * 本分区内有未保存改动时在分隔线后挂一句提示——用户可能正停在别的分区上。
 */
function SectionHeading({ group }: { group: NavGroup }) {
  const { dirtyIds } = useSysConfigBus();
  const dirtyCount = group.items.filter((i) => i.id in dirtyIds).length;

  return (
    <div className="sc-sec" id={group.secId}>
      <h3>{group.title}</h3>
      <div className="sc-rule" />
      {dirtyCount > 0 && <span className="sc-sec-dirty">{dirtyCount} 项未保存</span>}
    </div>
  );
}

/** 按 title 取分组，正文各段据此渲染标题（拿不到就说明上面的常量被改坏了） */
function groupOf(title: string): NavGroup {
  const g = NAV_GROUPS.find((x) => x.title === title);
  if (!g) throw new Error(`NAV_GROUPS 缺少分组「${title}」`);
  return g;
}

/* ------------------------------------------------------------------ *
 * 页面主体
 * ------------------------------------------------------------------ */

function SysConfigBody() {
  const rootRef = useRef<HTMLDivElement>(null);
  const topbarRef = useRef<HTMLDivElement>(null);
  const mainRef = useRef<HTMLDivElement>(null);

  const { anchorOffset, scrollParent } = useStickyGeometry(rootRef, topbarRef);
  const contentRevision = useContentRevision(mainRef);
  const active = useActiveAnchor(ALL_ANCHOR_IDS, anchorOffset, scrollParent, contentRevision);

  // 从其它页面带锚点跳进来（如 #sec-ndp-model-slots）：
  // 卡片异步加载，首帧时目标可能还没有高度，稍等后再滚动定位。
  useEffect(() => {
    const id = decodeURIComponent(window.location.hash.slice(1));
    if (!id) return undefined;
    const timer = window.setTimeout(() => {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 400);
    return () => window.clearTimeout(timer);
  }, []);

  return (
    <div className="sc-root" ref={rootRef}>
      <TopBar innerRef={topbarRef} />

      <div className="sc-shell">
        <AnchorNav active={active} />

        <div className="sc-main" ref={mainRef}>
          {/* ── 运行包与版本：四项单行值，行式列表 ── */}
          <SectionHeading group={groupOf('运行包与版本')} />
          <div className="sc-rows">
            <ConfigRow<GenerateScenePackageUrlVo>
              id="cfg-generate-scene"
              title="场景生成运行包 URL"
              index={0}
              description={(
                <span>
                  系统优先读取 Redis 中保存的 <code>package_url</code>；当 Redis 未配置或读取异常时，
                  自动回退到代码内置的兜底值。修改后立即对后续 generate-scene 任务生效。
                </span>
              )}
              placeholder="bos://... 或 s3://..."
              fetcher={getGenerateScenePackageUrl}
              saver={updateGenerateScenePackageUrl}
              resetter={resetGenerateScenePackageUrl}
              validate={validatePackageUrl}
            />

            <ConfigRow<GenerateScenePackageUrlVo>
              id="cfg-perception-generate-scene"
              title="感知场景生成运行包 URL"
              index={1}
              description="感知场景（SCENE_PE）使用的算法包，包内需要包含 scene_perception_qc_runner.py。"
              placeholder="bos://... 或 s3://..."
              fetcher={getGeneratePerceptionScenePackageUrl}
              saver={updateGeneratePerceptionScenePackageUrl}
              resetter={resetGeneratePerceptionScenePackageUrl}
              validate={validatePackageUrl}
            />

            <ConfigRow<GenerateScenePackageUrlVo>
              id="cfg-perception-scene-tree"
              title="感知场景子树根节点"
              index={2}
              description="场景树里「感知场景」目录的节点 id。感知入库只能选这棵子树下的场景集。留空表示尚未配置。"
              placeholder="正整数，留空表示未配置"
              fetcher={getPerceptionSceneTreeRootId}
              saver={updatePerceptionSceneTreeRootId}
              resetter={resetPerceptionSceneTreeRootId}
              validate={value => {
                const text = value.trim();
                if (!text) return null;
                return /^[1-9]\d*$/.test(text) ? null : '请填写正整数，或留空';
              }}
            />

            <ConfigRow<GenerateScenePackageUrlVo>
              id="cfg-scene-preview"
              title="场景预览运行包 URL"
              index={1}
              description={(
                <span>
                  场景预览（<code>scene_preview.py --upload</code>）使用的运行包；
                  与 generate-scene 同体系，可单独切版本而不影响场景生成主链路。
                  修改后立即对后续 scene-preview 任务生效。
                </span>
              )}
              placeholder="bos://... 或 s3://..."
              fetcher={getGenerateScenePreviewPackageUrl}
              saver={updateGenerateScenePreviewPackageUrl}
              resetter={resetGenerateScenePreviewPackageUrl}
              validate={validatePackageUrl}
            />

            <ConfigRow<AlgorithmPackageUrlTemplateVo>
              id="cfg-algorithm-template"
              title="数据中台包 URL 模板"
              index={2}
              description={(
                <span>
                  前端「中台导入」模式（<code>buildType=package_neodata</code>）下发任务时，将{' '}
                  <code>algorithmVersion</code> 代入 <code>{'{version}'}</code> 占位符拼装完整 OBS 路径
                  （默认 <code>.tgz</code> 后缀）。模板必须含 <code>{'{version}'}</code>，且以{' '}
                  <code>bos://</code> / <code>s3://</code> / <code>obs://</code> 开头；修改后立即对后续任务生效。
                </span>
              )}
              placeholder="obs://auto-packages/.../x86_64_{version}-Orin6.0.9.0.tgz"
              fetcher={getAlgorithmPackageUrlTemplate}
              saver={updateAlgorithmPackageUrlTemplate}
              resetter={resetAlgorithmPackageUrlTemplate}
              validate={validatePackageUrlTemplate}
            />

            <ConfigRow<DailyBaseVersionVo>
              id="cfg-daily-base-version"
              title="daily bazel base-version"
              index={3}
              description={(
                <span>
                  daily 仿真创建 bazel 编译任务时读取的 <code>base_version</code>。系统优先读取
                  Redis 配置；未配置时回退到代码默认值（<code>settings.daily.base-version</code>）。
                  修改后立即对后续 daily 任务生效。
                </span>
              )}
              placeholder="如 20260609.release.10.10.8"
              fetcher={getDailyBaseVersion}
              saver={updateDailyBaseVersion}
              resetter={resetDailyBaseVersion}
              validate={validateDailyBaseVersion}
            />
          </div>

          {/* ── Bazel 编译：两张全宽 JSON 面板 ── */}
          <SectionHeading group={groupOf('Bazel 编译')} />
          <div className="sc-panels">
            <BazelPresetsCard index={4} />
            <ModuleRegistryCard index={5} />
          </div>

          {/* ── 评测器：两张全宽 JSON 面板 ── */}
          <SectionHeading group={groupOf('评测器')} />
          <div className="sc-panels">
            <EvaluatorWhitelistCard index={6} />
            <EvaluatorMetricAggCard index={7} />
          </div>

          {/* ── 预测数据：一项布尔开关。放在 GC 之前——它决定「发不发」，
              是这一组里最先要看的那个；GC 决定「留多久」，是它下游的清理策略。 ── */}
          <SectionHeading group={groupOf('预测数据')} />
          <div className="sc-rows">
            <ConfigSwitchRow<PredictionOpenEnabledVo>
              id="cfg-prediction-open-enabled"
              title="预测数据开放开关"
              index={8}
              description={(
                <span>
                  只作用于<b>引擎侧取数接口</b>（<code>/prediction-open/latest-version</code>）。
                  关闭后该接口直接返回「无数据」，仿真回退用 record 自带的预测节点把场景
                  跑完（<b>不中断、不判失败</b>）；平台内部的预测版本列表与版本运维照常可用。
                  保存后 ≤5s 对新发起的仿真生效，已在飞的仿真按原样跑完。
                </span>
              )}
              onText="预测数据正常发放给仿真引擎"
              offText="已停发：仿真将回退用 record 自带的预测节点"
              fetcher={getPredictionOpenEnabled}
              saver={updatePredictionOpenEnabled}
              resetter={resetPredictionOpenEnabled}
              validate={validatePredictionOpenEnabled}
            />
            <ConfigSelectRow<PredictionJobDefaultVersionVo>
              id="cfg-prediction-job-default-version"
              title="创建任务默认预测数据版本"
              index={9}
              description={(
                <span>
                  创建 <b>LPL（PNC 仿真）</b>任务时，「预测数据版本」输入框<code>留空</code>所采用的值
                  （前端默认值也读这一项）。保存后对新发起的任务生效。
                </span>
              )}
              // 值域 = 后端白名单（``default`` / ``latest``），这里不提供自由输入：
              // 写进别的串后端会 400，而下拉里根本选不出来，比事后报错更早一步。
              options={[
                {
                  value: 'latest',
                  label: 'latest',
                  hint: '该任务所属 daily tag 下当前生效的版本；还没有人工设过当前版本时回落 default',
                },
                {
                  value: 'default',
                  label: 'default',
                  hint: '使用 record 包内自带的原始预测（不查平台数据）',
                  warn: true,
                },
              ]}
              fetcher={getPredictionJobDefaultVersion}
              saver={updatePredictionJobDefaultVersion}
              resetter={resetPredictionJobDefaultVersion}
              validate={validatePredictionJobDefaultVersion}
            />
          </div>

          <SectionHeading group={groupOf('任务参数')} />
          <div className="sc-panels">
            <JobCustomParametersCard index={12} />
          </div>

          <SectionHeading group={groupOf('产物桶')} />
          <div className="sc-panels">
            <OutputBucketRoutingCard index={13} />
          </div>

          {/* ── Case-Flow 查询：一项单行值。上限是全局的（所有用户 / 所有批次 /
              三个 region 共用一个池子），跨 worker 经 Redis 租约共同受限。 ── */}
          <SectionHeading group={groupOf('Case-Flow 查询')} />
          <div className="sc-rows">
            <ConfigRow<CaseflowQueryConcurrencyVo>
              id="cfg-caseflow-query-concurrency"
              title="查询并发上限"
              index={14}
              description={(
                <span>
                  所有用户、所有批次的 case 查询共享这一个全局上限（含批量生成时的并发预取）。
                  多 worker / 多实例经 Redis 租约共同受限；必须是 <b>1–200</b> 的整数，默认{' '}
                  <code>30</code>。保存后 ≤5s 对新发起的查询生效，已在飞的查询按原上限跑完。
                  调大前先确认 Case-Flow 上游配额。
                </span>
              )}
              placeholder="如 30"
              fetcher={getCaseflowQueryConcurrency}
              saver={updateCaseflowQueryConcurrency}
              resetter={resetCaseflowQueryConcurrency}
              validate={validateCaseflowQueryConcurrency}
            />
          </div>

          <SectionHeading group={groupOf('模型同步')} />
          <div className="sc-panels">
            <NdpModelSlotsCard index={15} />
            <ModelCategoriesCard index={16} />
            <ModelUploadNamesCard index={17} />
          </div>

          {/* ── 运维操作：三列等宽，amber 色条与配置区区隔 ──
              stagger 序号接着配置区往下排（原实现运维卡从 5 重新开始，
              和配置卡的 5/6 撞号，入场动画节奏是乱的）。 */}
          <SectionHeading group={groupOf('运维操作')} />
          <div className="sc-ops-grid">
            <div className="sc-anchor" id="ops-daily-job">
              <DailyJobTriggerCard index={18} />
            </div>
            <div className="sc-anchor" id="ops-bulk-refresh">
              <BulkRefreshRunningJobsCard index={19} />
            </div>
            <div className="sc-anchor" id="ops-map-generate">
              <MapGenerateRecentCard index={20} />
            </div>
            <div className="sc-anchor" id="ops-partition-cleanup">
              <PartitionCleanupCard index={21} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function SysConfigPage() {
  return (
    <SysConfigBusProvider>
      <SysConfigBody />
    </SysConfigBusProvider>
  );
}
